import { scaleOrdinal } from "d3-scale";
import React from "react";
import { defineMessage, useIntl } from "react-intl";
import { LineChart } from "../charts/LineChart";
import { withFigureIO } from "../components/figure";
import { metadata } from "../data/data_access_fig_COMPLETION";
import { datasets } from "../data/data_access_fig_COMPLETION_datasets";
import { countryIdIso, foldGeoEntity, mkGemEntityDecoder, useCountryEntities, useFigureControlItems, useNamedEntitiesWithLevel, useSingleSelectState } from "../domain";
import { useMedia } from "../hooks";
import { insertionOrderSet, unsafeFromArray } from "../lib";
import * as M from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { Ar, io, O, pipe } from "../prelude";
export * from "../data/data_access_fig_COMPLETION";
const DEFAULT_COUNTRY = "AGO";
const Completion = io.keyof({
    timely: null,
    ultimately: null
});
const completionLookup = {
    timely: defineMessage({
        id: "fig.access.COMPLETION.timely",
        defaultMessage: "timely"
    }),
    ultimately: defineMessage({
        id: "fig.access.COMPLETION.ultimately",
        defaultMessage: "ultimately"
    })
};
export const Data = mkGemEntityDecoder([
    "ind_id",
    "value",
    "year",
    "level"
], {
    completion: Completion
});
export const Chart = ({ data })=>{
    const intl = useIntl();
    const screenSDown = !useMedia(breakpoints.s);
    const screenMDown = !useMedia(breakpoints.m);
    const [selectedCountry, actions] = useSingleSelectState("country");
    const entityData_ = useNamedEntitiesWithLevel(data);
    const entityData = React.useMemo(()=>entityData_.map((x)=>({
                ...x,
                category: `${x.entity_name} (${intl.formatMessage(completionLookup[x.completion])})`
            })), [
        intl,
        entityData_
    ]);
    const countryData = useCountryEntities(entityData);
    const countryControlItems = useFigureControlItems(countryData, (x)=>countryIdIso.unwrap(x.id), (x)=>x.entity_name);
    React.useEffect(()=>{
        actions.setSelectionControl("country", {
            type: "SingleSelect",
            selected: pipe(countryControlItems, Ar.findFirst((x)=>x.value === DEFAULT_COUNTRY), O.alt(()=>Ar.head(countryControlItems)), O.map((x)=>x.value))
        }, countryControlItems);
    }, [
        actions,
        countryControlItems
    ]);
    const chartData = React.useMemo(()=>{
        return unsafeFromArray(entityData.filter(foldGeoEntity({
            country: ({ id })=>pipe(selectedCountry, O.fold(()=>false, (x)=>x === countryIdIso.unwrap(id))),
            region: ({ entity_type })=>entity_type === "world"
        })));
    }, [
        entityData,
        selectedCountry
    ]);
    const legendEntries = React.useMemo(()=>insertionOrderSet(chartData, (x)=>x.category), [
        chartData
    ]);
    const colorScale = React.useMemo(()=>scaleOrdinal(Ar.chunksOf(2)(M.discretePalette)// Careful: b could be undefined for different chunk sizes / palette lengths
        .flatMap(([a, b])=>[
                a,
                b,
                a,
                b
            ])).domain(legendEntries), [
        legendEntries
    ]);
    return <LineChart tLabel={(s)=>s} height={screenSDown ? M.chartHeight.xs : screenMDown ? M.chartHeight.s : M.chartHeight.m} endLabel={!screenMDown} x="year" timeParse="%Y" timeFormat="%Y" numberFormat=".0%" column="level_name" columns={3} columnSort="none" category="datum.category" colorLegend colorScale={colorScale} values={chartData} yTicks={[
        0,
        0.5,
        1
    ]} showTooltip markStyle={(x)=>x.entity_type === "world" ? "muted" : "normal"}/>;
};
export default withFigureIO({
    url: new URL("../data/data_access_fig_COMPLETION.json", import.meta.url).href,
    csv: new URL("../data/data_access_fig_COMPLETION.zip", import.meta.url).href,
    xlsx: new URL("../data/data_access_fig_COMPLETION.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    datasets
});
